import React from 'react'
import { connect } from 'react-redux'
import { Formik, ErrorMessage } from 'formik'
import { find } from 'lodash'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { closeModal } from 'store/actions/modals'
import { Button, Select, Textarea, Form } from 'components/Forms'
import ShiftEligibilitySummary from 'components/ContactLists/ShiftEligibilitySummary'
import AllShiftsDeleted from 'Manager/Contact/components/AllShiftsDeleted'
import { getUserName, hasAccessToThis, parsePhone } from 'services/Utilities'
import { Alert, Button as MerinioButton, Card, List } from 'ui'
import { useTranslation } from 'react-i18next'

const ContactUserAddHistoryModal = ({ id, contactUsers, contactList, closeModal, usersById }) => {
  const { t } = useTranslation('legacy')
  const { userId } = find(contactUsers, { _id: id })
  const { phoneNumbers = [] } = usersById[userId]

  const statuses = [
    { _id: 'COMPLETE_IN_PERSON', name: t('manager.contactLists.HISTORY.COMPLETE_IN_PERSON') },
    { _id: 'COMPLETE_OVER_PHONE', name: t('manager.contactLists.HISTORY.COMPLETE_OVER_PHONE') },
    { _id: 'OTHER_NOTES', name: t('manager.contactLists.HISTORY.OTHER_NOTES') }
  ]

  const answers = [
    { _id: 'NOTE', name: t('manager.contactLists.HISTORY.NOTE') },
    { _id: 'BUSY', name: t('manager.contactLists.HISTORY.BUSY') },
    { _id: 'ACCEPTED', name: t('manager.contactLists.HISTORY.ACCEPTED') },
    { _id: 'REFUSED', name: t('manager.contactLists.HISTORY.REFUSED') },
    { _id: 'LEFT_MESSAGE', name: t('manager.contactLists.HISTORY.LEFT_MESSAGE') },
    { _id: 'OTHER_PERSON_ACCEPTED', name: t('manager.contactLists.HISTORY.OTHER_PERSON_ACCEPTED') },
    { _id: 'OTHER_PERSON_REFUSED', name: t('manager.contactLists.HISTORY.OTHER_PERSON_REFUSED') },
    { _id: 'OTHER_PERSON_LEFT_MESSAGE', name: t('manager.contactLists.HISTORY.OTHER_PERSON_LEFT_MESSAGE') },
    { _id: 'NO_ANSWER', name: t('manager.contactLists.HISTORY.NO_ANSWER') },
    { _id: 'NO_SERVICE', name: t('manager.contactLists.HISTORY.NO_SERVICE') },
    { _id: 'NOT_AVAILABLE_ACCEPTED', name: t('manager.contactLists.HISTORY.NOT_AVAILABLE_ACCEPTED') },
    { _id: 'NOT_AVAILABLE_REFUSED', name: t('manager.contactLists.HISTORY.NOT_AVAILABLE_REFUSED') },
    { _id: 'CONTACTED_BY_MISTAKE', name: t('manager.contactLists.HISTORY.CONTACTED_BY_MISTAKE') }
  ]
  const changePriority = async ({ status, answer, comment, acceptedShift }, actions) => {
    let command = 'MANUAL_HISTORY'
    let historyEntry = { status, answer, comment }

    switch (answer) {
      case 'ACCEPTED':
      case 'OTHER_PERSON_ACCEPTED':
        command = 'MANUAL_ACCEPTED'
        historyEntry.acceptedShift = acceptedShift
        break
      case 'REFUSED':
      case 'OTHER_PERSON_REFUSED':
        command = 'MANUAL_REFUSED'
        break
      case 'CONTACTED_BY_MISTAKE':
        command = 'CONTACTED_BY_MISTAKE'
        break
      default:
    }

    const { success } = await Api.put(`contact/users/${command}`, { _id: id, historyEntry })
    if (!success) {
      actions.setStatus({ type: 'danger', msg: 'manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL.ERROR_SAVING_ENTRY' })
      actions.setSubmitting(false)
    } else closeModal()
  }

  return (
    <BaseModal hideButtons title={`manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL`} titleVar={{ name: getUserName(userId) }}>
      <Formik
        enableReinitialize={true}
        initialValues={{ status: 'COMPLETE_IN_PERSON', answer: 'NOTE', comment: '', acceptedShift: '' }}
        onSubmit={changePriority}
        validate={values => {
          let errors = {}
          if (['ACCEPTED', 'OTHER_PERSON_ACCEPTED'].includes(values.answer) && values.acceptedShift.length === 0)
            errors.acceptedShift = 'manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL.PLEASE_SELECT_SHIFT'
          return errors
        }}
      >
        {({ values, isSubmitting, status, setStatus, setFieldValue }) => {
          return (
            <Form className="mt-2">
              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>

              {/* Show user contact info */}
              {hasAccessToThis('userSeeContactInfo') ? (
                <>
                  <label className="control-labe">{t('manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL.CONTACT_INFO')}</label>
                  <List className="mb-2">
                    {phoneNumbers.map(({ phoneNumber, type }, index) => (
                      <List.Item key={index}>
                        <div>{parsePhone(phoneNumber)}</div>
                        <small className="text-muted d-block">{t(type)}</small>
                      </List.Item>
                    ))}
                  </List>
                </>
              ) : null}

              <Select module="manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL" name="status" options={statuses} required />
              <Select module="manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL" name="answer" options={answers} required />
              {['ACCEPTED', 'OTHER_PERSON_ACCEPTED'].includes(values.answer) ? (
                <Card maxHeight="20rem" className="mb-2">
                  <List className="list-group-flush">
                    {contactList.originalShifts.map(shift => {
                      const isSelected = values.acceptedShift === shift._id
                      return (
                        <ShiftEligibilitySummary
                          key={shift._id}
                          actionButton={
                            <MerinioButton
                              disabled={isSelected}
                              type="button"
                              color={isSelected ? 'primary' : 'secondary'}
                              outlined={!isSelected}
                              onClick={() => setFieldValue('acceptedShift', shift._id)}
                            >
                              {isSelected ? <i className="fa fa-check-circle mr-1" /> : null}
                              {t(`manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL.${isSelected ? 'SHIFT_SELECTED' : 'SELECT_SHIFT'}`)}
                            </MerinioButton>
                          }
                          shift={shift}
                          contactUserId={id}
                        />
                      )
                    })}
                    <AllShiftsDeleted originalShifts={contactList.originalShifts} />
                  </List>
                  <ErrorMessage name="acceptedShift" render={msg => <span className="text-danger"> {t(msg)}</span>} />
                </Card>
              ) : null}
              <Alert variant="warning" className="mt-2" show={['REFUSED', 'OTHER_PERSON_REFUSED'].includes(values.answer)}>
                {t(`manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL.REFUSED_DESCRIPTION`)}
              </Alert>
              <Textarea
                module="manager.contactLists.CONTACT_USER_ADD_HISTORY_MODAL"
                name="comment"
                required={
                  values.status === 'OTHER_NOTES' || ['NOTE', 'ACCEPTED', 'REFUSED', 'OTHER_PERSON_ACCEPTED', 'OTHER_PERSON_REFUSED'].includes(values.answer)
                }
              />
              <div className="mt-2">
                <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
                <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => closeModal()} />
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

const mapStateToProps = ({ contactLists, resources }) => ({
  contactList: contactLists.currentContactList,
  contactUsers: contactLists.currentContactUsers,
  usersById: resources.usersById
})
const mapDispatchToProps = dispatch => ({
  closeModal: () => dispatch(closeModal())
})

export default connect(mapStateToProps, mapDispatchToProps)(ContactUserAddHistoryModal)
